import styled, { css } from 'styled-components';

import { CommonCssProps, StyleSwitchProps } from './Heading.types';

const commonCss = css<CommonCssProps>`
  display: flex;
  align-items: center;
  color: ${({ theme, color }) => (color ? color : theme.colors.neutral.primaryNavy)};
  background-color: ${({ $bg }) => ($bg ? $bg : 'initial')};
  flex-direction: ${({ $flexDirection }) => $flexDirection || 'row'};

  ${({ $multilineEllipses }) =>
    $multilineEllipses &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${$multilineEllipses};
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    `}
`;

const h1Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.DMSerif};
  font-size: 36px;
  font-weight: 400;
  line-height: 1.11;
  letter-spacing: 0.24px;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 32px;')}

  @media ${({ theme }) => theme.queries.minDesktop} {
    font-size: 56px;
    line-height: 64px;
  }
`;

const h2Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.DMSerif};
  font-size: 28px;
  font-weight: 400;
  line-height: 1.14;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 24px;')}

  @media ${({ theme }) => theme.queries.minDesktop} {
    font-size: 32px;
    line-height: 40px;
    letter-spacing: 0.32px;
  }
`;

const h3Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.DMSerif};
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 20px;')}

  @media ${({ theme }) => theme.queries.minDesktop} {
    ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 16px;')}
  }
`;

const h4Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.Satoshi};
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 20px;')}

  @media ${({ theme }) => theme.queries.minDesktop} {
    line-height: 1.333;
    font-size: 25px;
    ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 16px;')}
    letter-spacing: unset;
  }
`;

const h5Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.Satoshi};
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 16px;')}

  @media ${({ theme }) => theme.queries.minDesktop} {
    line-height: 24px;
    letter-spacing: 0.32px;
  }
`;

const h6Css = css<CommonCssProps>`
  ${({ theme }) => theme.fonts.Satoshi};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.143;
  letter-spacing: 0.48px;
  ${({ $noMargin }) => ($noMargin ? '' : 'margin-bottom: 16px;')}
  text-transform: uppercase;

  @media ${({ theme }) => theme.queries.minDesktop} {
    font-weight: 400;
    letter-spacing: 0.48px;
    font-size: 12px;
    line-height: 16px;
  }
`;

// By passing in the styledAs prop, we can dynamically switch between the different heading styles for every heading tag.
// This allows us to use "div" styled as an h1, for example where semantically it doesn't make sense an h1 but visually it does.
// It also allows us to use "h1" styled as an h6, for example where semantically it makes sense as an h1 but visually it makes sense as an h6.
type HeadingStyledProps = CommonCssProps &
  CommonCssProps & {
    $styledAs: StyleSwitchProps['styledAs'];
    $noMargin?: boolean;
  };

// native heading tags
export const H1 = styled.h1<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs, $transform }) => {
    switch ($styledAs) {
      case 'h1':
        return css`
          ${h1Css}
          text-transform: ${$transform};
        `;
      case 'h2':
        return css`
          ${h2Css}
          text-transform: ${$transform};
        `;
      case 'h3':
        return css`
          ${h3Css}
          text-transform: ${$transform};
        `;
      case 'h4':
        return css`
          ${h4Css}
          text-transform: ${$transform};
        `;
      case 'h5':
        return css`
          ${h5Css}
          text-transform: ${$transform};
        `;
      case 'h6':
        return css`
          ${h6Css}
          text-transform: ${$transform};
        `;
      default:
        return css`
          ${h1Css}
          text-transform: ${$transform};
        `;
    }
  }}
`;
export const H2 = styled.h2<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;
export const H3 = styled.h3<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;
export const H4 = styled.h4<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;
export const H5 = styled.h5<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;
export const H6 = styled.h6<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;

// div styled as heading
export const DivAsHeading = styled.div<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}
`;

export const SpanAsHeading = styled.span<HeadingStyledProps>`
  ${commonCss}
  ${({ $styledAs }) => {
    switch ($styledAs) {
      case 'h1':
        return h1Css;
      case 'h2':
        return h2Css;
      case 'h3':
        return h3Css;
      case 'h4':
        return h4Css;
      case 'h5':
        return h5Css;
      case 'h6':
        return h6Css;
      default:
        return h1Css;
    }
  }}

  // override margin since this is an inline element
  margin-bottom: 0px;
  @media ${({ theme }) => theme.queries.minDesktop} {
    margin-bottom: 0px;
  }
`;
